import { AxiosRequestConfig, isAxiosError } from 'axios'
import { TEntity, TLanguage } from 'contexts/app-context/types'
import { Answer } from 'pages/frequent-questions/components/faq-card'

import { httpRequest } from '../../../common/api'
import { GetFAQsResponse } from '../types/FrequentQuestions.types'

type TFaqLanguage = 'en' | 'es' | 'pt'

type TFaqCategory = 'Central de Privacidade' | 'Privacy Center' | 'Centro de Privacidad'

type TFaqOrganization = 'ia' | 'pf'

const FAQ_BASE_URL = process.env.REACT_APP_INTER_CLOUD_SEARCH_URL
const ARTICLES_URL = process.env.REACT_APP_INTER_ARTICLES_URL

const contextLanguageMapper: Record<TLanguage, TFaqLanguage> = {
  'pt-BR': 'pt',
  'en-US': 'en',
  'es-ES': 'es',
}

// If entity = BR, there's only one category as app supports only PT, so far. Else, there are 3 categories as app supports EN, ES and PT
const categorySearchParamMapper: Record<TEntity, Record<TLanguage, TFaqCategory>> = {
  BR: {
    'pt-BR': 'Central de Privacidade',
    'en-US': 'Central de Privacidade',
    'es-ES': 'Central de Privacidade',
  },
  US_BRANCH: {
    'pt-BR': 'Central de Privacidade',
    'en-US': 'Privacy Center',
    'es-ES': 'Centro de Privacidad',
  },
}

// If entity = BR | SECURITIES complement is "pessoa fisica - pf", if entity = US_BRANCH | PAYMENTS complement is "international account - ia"
const accountTypeMapper: Record<TEntity, TFaqOrganization> = {
  BR: 'pf',
  US_BRANCH: 'ia',
}

export const getFAQs = async (text: string, language: TLanguage, userSelectedEntity: TEntity) => {
  const searchParams1 = `(near '${text}')`
  const searchParams2 = `(phrase field='category_title' '${categorySearchParamMapper[userSelectedEntity][language]}')`
  const searchParams3 = `contexts: '${accountTypeMapper[userSelectedEntity]}'`

  const frequentQuestionQueryParams = new URLSearchParams({
    q: `(and ${searchParams1} ${searchParams2} ${searchParams3})`,
    sort: 'title asc',
    return: 'title',
    size: '15',
    'q.parser': 'structured',
  })

  try {
    const axiosConfig: AxiosRequestConfig = {
      method: 'GET',
      baseURL: FAQ_BASE_URL,
      url: `/api/babi/faq/search?${frequentQuestionQueryParams.toString()}`,
      headers: {
        'X-Inter-Lang': contextLanguageMapper[language],
        'X-Inter-Ou': accountTypeMapper[userSelectedEntity],
      },
    }

    return await httpRequest<GetFAQsResponse>(axiosConfig)
  } catch (error: unknown) {
    if (isAxiosError(error)) throw new Error(error.message)

    throw new Error('Unknown error')
  }
}

export const getFAQAnswer = async (
  id: string,
  language: TLanguage,
  userSelectedEntity: TEntity,
): Promise<Answer> => {
  // some IDs are '0160b620-ff9c-48e7-a435-166377da6ba4_en', but just the content before _ is needed
  const [formattedId] = id.split('_')

  try {
    const axiosConfig: AxiosRequestConfig = {
      method: 'GET',
      baseURL: ARTICLES_URL,
      url: `/${accountTypeMapper[userSelectedEntity]}/${contextLanguageMapper[language]}/${formattedId}.json`,
      headers: {
        'X-Inter-Lang': contextLanguageMapper[language],
        'X-Inter-Ou': accountTypeMapper[userSelectedEntity],
      },
    }

    return await httpRequest<Answer>(axiosConfig)
  } catch (error: unknown) {
    if (isAxiosError(error)) throw new Error(error.message)

    throw new Error('Unknown error')
  }
}
